import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Button,
  Flex,
  SimpleGrid,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import type { IconType } from "react-icons";
import {
  FiAlertTriangle,
  FiCheckCircle,
  FiClock,
  FiHelpCircle,
  FiPackage,
  FiRotateCcw,
  FiSearch,
} from "react-icons/fi";

import {
  DataTable,
  EmptyState,
  Meter,
  MotionReveal,
  Pagination,
  SearchField,
  SectionCard,
  StatCard,
  StatSkeleton,
  TableSkeleton,
  type Tone,
} from "../ui";
import { StockAnalysisItem } from "../../types/analysisDataTypes";
import { formatIsoDate, formatQuantity } from "../../utils/formatNumber";

const LOW_STOCK_DAYS = 7;
const ITEMS_PER_PAGE = 8;
// The days-left meter is full at a month of cover
const METER_MAX_DAYS = 30;

type StockStatus = "reorder" | "low" | "ok" | "unknown";

const STATUSES: Record<
  StockStatus,
  { label: string; description: string; tone: Tone; icon: IconType }
> = {
  reorder: {
    label: "Reorder now",
    description: "Less than a day of stock left",
    tone: "danger",
    icon: FiAlertTriangle,
  },
  low: {
    label: "Running low",
    description: `${LOW_STOCK_DAYS} days or less left`,
    tone: "warning",
    icon: FiClock,
  },
  ok: {
    label: "OK",
    description: `More than ${LOW_STOCK_DAYS} days left`,
    tone: "success",
    icon: FiCheckCircle,
  },
  unknown: {
    label: "Not enough data",
    description: "Too few orders to estimate",
    tone: "neutral",
    icon: FiHelpCircle,
  },
};

// Tiles (and their filters); "ok" items are still listed in the table, they just get no tile
const STATUS_ORDER: StockStatus[] = ["reorder", "low", "unknown"];

const COLUMNS = [
  "Item",
  "Est. days left",
  "Usage / day",
  "Last order",
  "Orders (30 days)",
  "Qty (30 days)",
  "Unit",
];

// NetSuite estimates days left from the usage rate; it is fractional, and
// negative once the stock is estimated to have run out. Whole days, rounded
// down so we never overstate what is left; the status is derived from the
// same number so the badge always agrees with the table.
const shownDaysLeft = (item: StockAnalysisItem) =>
  item.est_stock_days_left == null ? null : Math.max(0, Math.floor(item.est_stock_days_left));

const stockStatus = (item: StockAnalysisItem): StockStatus => {
  const daysLeft = shownDaysLeft(item);
  if (daysLeft === null) return "unknown";
  if (daysLeft === 0) return "reorder";
  if (daysLeft <= LOW_STOCK_DAYS) return "low";
  return "ok";
};

// Most urgent first; items without an estimate last
const byUrgency = (a: StockAnalysisItem, b: StockAnalysisItem) =>
  (a.est_stock_days_left ?? Infinity) - (b.est_stock_days_left ?? Infinity) ||
  b.days_since_last_order - a.days_since_last_order;

const pluralDays = (days: number) => (days === 1 ? "1 day" : `${days} days`);

interface StockAnalysisProps {
  items: StockAnalysisItem[] | undefined;
  isPending: boolean;
  isError: boolean;
  // Optional: shows a Retry button in the error state when the parent passes a refetch
  onRetry?: () => void;
}

const StockAnalysis: React.FC<StockAnalysisProps> = ({ items, isPending, isError, onRetry }) => {
  const [statusFilter, setStatusFilter] = useState<StockStatus | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);

  const statusCounts = useMemo(() => {
    const counts: Record<StockStatus, number> = { reorder: 0, low: 0, ok: 0, unknown: 0 };
    items?.forEach((item) => counts[stockStatus(item)]++);
    return counts;
  }, [items]);

  const filteredItems = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return (items ?? [])
      .filter((item) => !statusFilter || stockStatus(item) === statusFilter)
      .filter(
        (item) =>
          !term ||
          item.item_name.toLowerCase().includes(term) ||
          item.display_name.toLowerCase().includes(term) ||
          item.description.toLowerCase().includes(term)
      )
      .sort(byUrgency);
  }, [items, statusFilter, searchTerm]);

  useEffect(() => setPage(1), [statusFilter, searchTerm]);

  // A refetch can shrink the list under the current page
  const pageCount = Math.max(1, Math.ceil(filteredItems.length / ITEMS_PER_PAGE));
  const currentPage = Math.min(page, pageCount);
  const currentItems = filteredItems.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const hasItems = Boolean(items?.length);

  const renderContent = () => {
    if (isPending) {
      return (
        <>
          <SimpleGrid columns={{ base: 1, sm: 3 }} gap={4} mb={6}>
            {STATUS_ORDER.map((status) => (
              <StatSkeleton key={status} />
            ))}
          </SimpleGrid>
          <TableSkeleton columns={COLUMNS} rows={5} size="sm" />
        </>
      );
    }

    // After a failed refetch keep showing the data we already have
    if (isError && !items) {
      return (
        <Alert status="error" alignItems="center" flexWrap="wrap" gap={2}>
          <AlertIcon />
          <AlertDescription flex="1" minW="16rem">
            We couldn't load your stock analysis. Please try again later.
          </AlertDescription>
          {onRetry && (
            <Button variant="ghost" size="sm" leftIcon={<FiRotateCcw />} onClick={onRetry}>
              Retry
            </Button>
          )}
        </Alert>
      );
    }

    if (!items?.length) {
      return (
        <EmptyState
          icon={FiPackage}
          title="No orders in the last 30 days"
          description="Stock estimates appear here once you have ordered items."
        />
      );
    }

    return (
      <>
        {/* One column on phones so the tile eyebrows ("Not enough data") never truncate */}
        <SimpleGrid columns={{ base: 1, sm: 3 }} gap={4} mb={6}>
          {STATUS_ORDER.map((status, index) => {
            const { label, description, tone, icon } = STATUSES[status];
            const isSelected = statusFilter === status;
            return (
              <MotionReveal key={status} delay={index * 0.04}>
                <StatCard
                  variant="tile"
                  tone={tone}
                  icon={icon}
                  label={label}
                  value={statusCounts[status]}
                  caption={description}
                  isSelected={isSelected}
                  onClick={() => setStatusFilter(isSelected ? null : status)}
                  h="full"
                  p={{ base: 4, lg: 5 }}
                />
              </MotionReveal>
            );
          })}
        </SimpleGrid>

        {currentItems.length === 0 ? (
          <EmptyState
            icon={FiSearch}
            title="No items match your filters"
            description="Try another search term or clear the tile filter."
            py={8}
          />
        ) : (
          <DataTable size="sm" label="Stock items">
            <Table size="sm">
              <Thead>
                <Tr>
                  <Th scope="col">Item</Th>
                  <Th scope="col" isNumeric>
                    Est. days left
                  </Th>
                  <Th scope="col" isNumeric>
                    Usage / day
                  </Th>
                  <Th scope="col">Last order</Th>
                  <Th scope="col" isNumeric>
                    Orders (30 days)
                  </Th>
                  <Th scope="col" isNumeric>
                    Qty (30 days)
                  </Th>
                  <Th scope="col">Unit</Th>
                </Tr>
              </Thead>
              <Tbody>
                {currentItems.map((item) => {
                  const status = stockStatus(item);
                  const daysLeft = shownDaysLeft(item);
                  return (
                    <Tr key={item.item_id}>
                      <Td whiteSpace="normal" minW={{ base: "150px", md: "220px" }}>
                        <Text fontWeight={600} color="text.primary">
                          {item.display_name || item.description}
                        </Text>
                        <Text textStyle="caption" color="text.muted">
                          {item.item_name}
                        </Text>
                      </Td>
                      <Td isNumeric>
                        {daysLeft === null ? (
                          "—"
                        ) : (
                          <Flex align="center" justify="flex-end" gap={2}>
                            <Text as="span" textStyle="numeral">
                              {daysLeft}
                            </Text>
                            <Meter
                              value={Math.min(daysLeft, METER_MAX_DAYS)}
                              max={METER_MAX_DAYS}
                              tone={STATUSES[status].tone}
                              w="56px"
                              label={`${daysLeft} of ${METER_MAX_DAYS} days`}
                            />
                          </Flex>
                        )}
                      </Td>
                      <Td isNumeric>
                        {item.est_usage_per_day == null
                          ? "—"
                          : formatQuantity(item.est_usage_per_day)}
                      </Td>
                      <Td>
                        <Text textStyle="numeral">{formatQuantity(item.last_order_qty)}</Text>
                        <Text textStyle="caption" color="text.muted" whiteSpace="nowrap">
                          {formatIsoDate(item.last_invoice_date)} ·{" "}
                          {item.days_since_last_order === 0
                            ? "today"
                            : `${pluralDays(item.days_since_last_order)} ago`}
                        </Text>
                      </Td>
                      <Td isNumeric>{item.orders_last_30_days}</Td>
                      <Td isNumeric>{formatQuantity(item.net_qty_last_30_days)}</Td>
                      <Td>{item.sale_unit}</Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </DataTable>
        )}

        {filteredItems.length > 0 && (
          <Pagination
            page={currentPage}
            pageSize={ITEMS_PER_PAGE}
            total={filteredItems.length}
            onChange={setPage}
          />
        )}
      </>
    );
  };

  return (
    <SectionCard
      folio="02"
      title="Stock analysis"
      lede="Estimated from your orders in the last 30 days"
      isLoading={isPending}
      toolbar={
        hasItems ? (
          <SearchField
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder="Search items"
            aria-label="Search stock items"
          />
        ) : undefined
      }
    >
      {renderContent()}
    </SectionCard>
  );
};

export default StockAnalysis;
